'use client'

import Link from 'next/link'
import { useRouter } from 'next/navigation'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Logo } from '@/components/brand/logo'

export default function Home() {
  const router = useRouter()

  return (
    <div className="min-h-screen bg-gradient-to-br from-muted-50 to-muted-100 dark:from-ink-900 dark:to-ink-800">
      <div className="container mx-auto px-4 py-8">
        {/* Hero Section */}
        <div className="max-w-6xl mx-auto">
          <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-brand-600 to-brand-800 dark:from-brand-700 dark:to-brand-900 shadow-2xl">
            <div className="absolute inset-0 bg-grid-white/10"></div>
            <div className="relative px-6 py-24 sm:px-12 sm:py-32 lg:px-16">
              <div className="mx-auto max-w-4xl text-center space-y-8">
                <div className="inline-block">
                  <Badge className="bg-white/20 text-white border-white/30 hover:bg-white/30 text-sm px-4 py-1.5">
                    Your Partner in Industrial Excellence
                  </Badge>
                </div>
                <h1 className="text-5xl md:text-6xl lg:text-7xl font-display font-bold text-white leading-tight">
                  Industrial Consulting
                  <span className="block mt-2 bg-clip-text text-transparent bg-gradient-to-r from-yellow-200 to-yellow-400">
                    Excellence
                  </span>
                </h1>
                <p className="text-xl md:text-2xl text-brand-50 max-w-3xl mx-auto leading-relaxed">
                  Transforming industrial operations through expert consulting,
                  process optimization, and innovative solutions that drive measurable results.
                </p>
                <div className="flex flex-col sm:flex-row gap-4 justify-center pt-4">
                  <Button
                    size="lg"
                    className="text-lg px-10 bg-white text-brand-600 hover:bg-brand-50 shadow-lg hover:shadow-xl transition-all duration-200"
                    onClick={() => router.push('/form')}
                  >
                    Submit Application
                  </Button>
                  <Button
                    size="lg"
                    variant="outline"
                    className="text-lg px-10 bg-transparent border-2 border-white text-white hover:bg-white/10 shadow-lg"
                    onClick={() => router.push('/track-status')}
                  >
                    Track Application Status
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Services Section */}
        <div className="grid md:grid-cols-3 gap-6 max-w-5xl mx-auto mt-20">
          <Card>
            <CardHeader>
              <CardTitle className="font-display">Process Optimization</CardTitle>
              <CardDescription>
                Streamline your manufacturing processes to improve efficiency
              </CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="font-display">Quality Management</CardTitle>
              <CardDescription>
                Implement robust quality management systems
              </CardDescription>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader>
              <CardTitle className="font-display">Technology Integration</CardTitle>
              <CardDescription>
                Leverage cutting-edge technologies and automation
              </CardDescription>
            </CardHeader>
          </Card>
        </div>

        {/* Why Choose Us Section */}
        <section className="max-w-5xl mx-auto mt-32 space-y-8">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
              Why Choose PICONE?
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              We bring decades of industrial expertise to help you achieve operational excellence
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 mt-12">
            <Card className="border-2 border-brand-200 dark:border-brand-700">
              <CardHeader>
                <CardTitle className="font-display text-2xl">Industry Expertise</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Our team has extensive experience across multiple industries including manufacturing,
                  automotive, pharmaceuticals, and food processing. We understand your unique challenges.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-brand-200 dark:border-brand-700">
              <CardHeader>
                <CardTitle className="font-display text-2xl">Proven Results</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  Track record of delivering measurable improvements in productivity, quality,
                  and cost efficiency. Our clients typically see 20-40% improvement in key metrics.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-brand-200 dark:border-brand-700">
              <CardHeader>
                <CardTitle className="font-display text-2xl">Customized Solutions</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  No two operations are the same. We develop tailored strategies that fit your
                  specific needs, constraints, and objectives.
                </p>
              </CardContent>
            </Card>

            <Card className="border-2 border-brand-200 dark:border-brand-700">
              <CardHeader>
                <CardTitle className="font-display text-2xl">Ongoing Support</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground">
                  We don&apos;t just consult and leave. Our team provides continuous support to ensure
                  sustained improvements and help you adapt to changing conditions.
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Our Approach Section */}
        <section className="max-w-5xl mx-auto mt-32 space-y-8">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
              Our Approach
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              A systematic methodology that delivers consistent results
            </p>
          </div>

          <div className="grid md:grid-cols-4 gap-6 mt-12">
            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold text-xl mb-4">
                  1
                </div>
                <CardTitle className="font-display">Assess</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Comprehensive analysis of your current operations, identifying bottlenecks and opportunities
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold text-xl mb-4">
                  2
                </div>
                <CardTitle className="font-display">Design</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Develop customized solutions aligned with your goals and constraints
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold text-xl mb-4">
                  3
                </div>
                <CardTitle className="font-display">Implement</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Execute improvements with minimal disruption to ongoing operations
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="w-12 h-12 rounded-full bg-brand-500 flex items-center justify-center text-white font-bold text-xl mb-4">
                  4
                </div>
                <CardTitle className="font-display">Optimize</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Continuous monitoring and refinement to ensure lasting success
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Statistics Section */}
        <section className="max-w-5xl mx-auto mt-32">
          <Card className="bg-gradient-to-r from-brand-500 to-brand-600 text-white border-0">
            <CardContent className="p-12">
              <div className="grid md:grid-cols-4 gap-8 text-center">
                <div>
                  <div className="text-5xl font-bold mb-2">500+</div>
                  <div className="text-brand-100">Projects Completed</div>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">98%</div>
                  <div className="text-brand-100">Client Satisfaction</div>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">35%</div>
                  <div className="text-brand-100">Avg. Efficiency Gain</div>
                </div>
                <div>
                  <div className="text-5xl font-bold mb-2">20+</div>
                  <div className="text-brand-100">Years of Experience</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </section>

        {/* Industries Served Section */}
        <section className="max-w-5xl mx-auto mt-32 space-y-8">
          <div className="text-center">
            <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-4">
              Industries We Serve
            </h2>
            <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
              Delivering excellence across diverse industrial sectors
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6 mt-12">
            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="font-display">Manufacturing</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Discrete and process manufacturing optimization, lean implementation,
                  and production planning
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="font-display">Automotive</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Supply chain optimization, quality systems, and just-in-time manufacturing
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="font-display">Pharmaceuticals</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  GMP compliance, validation, quality control, and process validation
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="font-display">Food & Beverage</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Food safety systems, HACCP, process optimization, and regulatory compliance
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="font-display">Chemicals</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Process safety, batch optimization, and environmental compliance
                </p>
              </CardContent>
            </Card>

            <Card className="hover:shadow-lg transition-shadow">
              <CardHeader>
                <CardTitle className="font-display">Energy & Utilities</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-muted-foreground text-sm">
                  Energy efficiency, asset management, and operational excellence
                </p>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Call to Action Section */}
        <section className="max-w-5xl mx-auto mt-32 mb-20">
          <Card className="bg-gradient-to-br from-muted-100 to-muted-200 dark:from-ink-800 dark:to-ink-700 border-2 border-brand-300 dark:border-brand-600">
            <CardContent className="p-12 text-center">
              <h2 className="text-4xl md:text-5xl font-display font-bold text-foreground mb-6">
                Ready to Transform Your Operations?
              </h2>
              <p className="text-xl text-muted-foreground max-w-2xl mx-auto mb-8">
                Let&apos;s discuss how we can help you achieve operational excellence
                and sustainable competitive advantage.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button
                  size="lg"
                  className="text-lg px-10 bg-brand-500 hover:bg-brand-600"
                  onClick={() => router.push('/form')}
                >
                  Schedule a Consultation
                </Button>
                <Button size="lg" variant="outline" className="text-lg px-10">
                  <Link href="/contact">Contact Us</Link>
                </Button>
              </div>
            </CardContent>
          </Card>
        </section>
      </div>
    </div>
  )
}
